'use client'

import { Avatar, Button, useIsMobile } from '@chain-zen/ui'
import { useEffect, useState } from 'react'

interface IAvatarsGridProps {
  avatarsPerPage?: number
  onAvatarClickAction: (avatarUrl: string) => void
  size?: 'sm' | 'md' | 'lg'
}

const TOTAL_AVATARS = 239
const MOBILE_AVATARS_PER_PAGE = 6
const DESKTOP_AVATARS_PER_PAGE = 9

export const shuffleArray = (array: number[]) => {
  // Simple deterministic shuffle: Reverse + Modulo-based swap
  const shuffled = [...array]
  for (let i = 0; i < array.length; i++) {
    const swapIndex = (i + 7) % array.length // Example deterministic swap logic
    ;[shuffled[i], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[i]]
  }
  return shuffled
}

interface IAvatarGridContentProps {
  avatarsPerPage: number
  currentIndex: number
  onAvatarClick: (avatarUrl: string) => void
  shuffledIndices: number[]
  size?: 'sm' | 'md' | 'lg'
}

const AVATAR_SIZE_CLASSES = {
  sm: 'h-10 w-10',
  md: 'h-16 w-16 sm:h-24 sm:w-24',
  lg: 'h-20 w-20 sm:h-28 sm:w-28'
}

function AvatarGridContent({
  currentIndex,
  avatarsPerPage,
  shuffledIndices,
  onAvatarClick,
  size = 'md'
}: IAvatarGridContentProps) {
  const avatarSizeClass = AVATAR_SIZE_CLASSES[size]

  return (
    <div className="grid grid-cols-3 gap-2 place-items-center mx-auto w-full max-w-md">
      {Array.from({ length: avatarsPerPage }).map((_, i) => {
        const arrayIndex = currentIndex * avatarsPerPage + i
        if (arrayIndex >= TOTAL_AVATARS) return null

        const avatarIndex = shuffledIndices[arrayIndex]
        const src = `https://chaine-images.s3.amazonaws.com/default-profile-avatars/${avatarIndex}.png`

        return (
          <div
            key={`avatar-${i}`}
            onClick={() => onAvatarClick(src)}
            className="cursor-pointer flex items-center justify-center relative group hover:scale-105 transition-transform duration-200"
          >
            <div
              className="absolute inset-0 rounded-full transition-all duration-200
              group-hover:ring-2 group-hover:ring-primary group-hover:ring-offset-2
              dark:ring-offset-gray-900 dark:group-hover:ring-primary-400"
            />
            <Avatar
              name={`default-profile-avatars-${avatarIndex}`}
              size="2xl"
              className={avatarSizeClass}
              variant="basic"
              src={src}
              aria-label={`default-profile-avatars-${avatarIndex}`}
              data-testid={'avatars'}
            />
          </div>
        )
      })}
    </div>
  )
}

export function AvatarsGrid({ onAvatarClickAction, size = 'md' }: IAvatarsGridProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const isMobile = useIsMobile()
  const avatarsPerPage = isMobile ? MOBILE_AVATARS_PER_PAGE : DESKTOP_AVATARS_PER_PAGE

  const shuffledIndices = shuffleArray(Array.from({ length: TOTAL_AVATARS }, (_, i) => i + 1))

  useEffect(() => {
    setCurrentIndex(0)
  }, [avatarsPerPage])

  const handlePrevious = () => {
    setCurrentIndex(currentIndex - 1)
  }

  const handleNext = () => {
    setCurrentIndex(currentIndex + 1)
  }

  const isLastPage = currentIndex >= Math.ceil(TOTAL_AVATARS / avatarsPerPage) - 1
  const isFirstPage = currentIndex === 0

  const isCompact = size === 'sm'

  return (
    <div className={`flex flex-col items-center w-full ${isCompact ? 'space-y-2' : 'space-y-4 sm:space-y-6'}`}>
      <AvatarGridContent
        currentIndex={currentIndex}
        avatarsPerPage={avatarsPerPage}
        shuffledIndices={shuffledIndices}
        onAvatarClick={onAvatarClickAction}
        size={size}
      />

      <div className="flex justify-center gap-2">
        <Button
          variant="outline"
          size={isCompact ? 'sm' : 'default'}
          onClick={handlePrevious}
          disabled={isFirstPage}
          className={isCompact ? 'min-w-[60px] text-xs' : 'min-w-[80px] sm:min-w-[100px]'}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size={isCompact ? 'sm' : 'default'}
          onClick={handleNext}
          disabled={isLastPage}
          className={isCompact ? 'min-w-[60px] text-xs' : 'min-w-[80px] sm:min-w-[100px]'}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
